import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import ordersService, { type AllDispatcher } from '../services/orders.service';
import { getRails, type RailOption } from '../services/rails.service';
import { listContacts, type Contact } from '../services/contacts.service';
import { EQUIPMENT_SIZES, EQUIPMENT_TYPES, CHASSIS_TYPES } from '../constants/orderFieldOptions';
import { CARRIER_DISPATCHERS } from '../utils/outsideCarrier';
import TypeAhead from './TypeAhead';
import { getBrokers, type LookupOption } from '../services/orderLookups.service';
import CarrierLookupField, { type CarrierValue } from './CarrierLookupField';
import PortLookupField, { type PortValue } from './PortLookupField';
import LocationLookupField, { type LocationValue } from './tablemaint/LocationLookupField';
import DateTimePicker from './DateTimePicker';
import { parseMdYDate, formatMdYDate, formatBackendDate } from '../utils/dateUtils';
import {
  EdiCreatePreview,
  getEdiCreatePreview,
  createEdiOrder,
} from '../services/pendingEdiOrders.service';

// =============================================================================
// EdiCreateModal — create a NEW pro from one accepted+templated pending EDI order
// (V1 edi_createpro form). The create-preview pre-fills every editable field
// (EDI value else the template's); the read-only "From EDI" panel shows what the
// EDI carried. Carrier is LOCKED unless the dispatcher is an outside carrier
// (CARRIER_DISPATCHERS). For IMPORT the ETA is required (pre-filled to today when
// the EDI carried none). On save the host runs createEdiOrder via onCreated.
// Portaled at z-[130] so it sits above the drill drawer.
// =============================================================================

interface Props {
  recno: number;
  title: string;
  onClose: () => void;
  /** Fired after a successful create with the new pro. */
  onCreated: (result: { recno: number; pronumber: number }) => void;
}

const LABEL = 'text-xs font-medium text-slate-600 dark:text-gray-300';
const INPUT =
  'w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none ' +
  'focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white disabled:bg-slate-100 ' +
  'disabled:text-slate-400 dark:disabled:bg-gray-900 dark:disabled:text-gray-500';

// The create-preview returns dates as ISO (YYYY-MM-DD); the app's date fields are MM/DD/YYYY
// (parseMdYDate / formatMdYDate). formatBackendDate converts back to ISO for the wire.
const isoToMdy = (v: any): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? ''));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : String(v ?? '');
};

// Orange cue (matches TypeAhead) when a template-copied value is no longer in its option list
// — e.g. the template's dispatcher was since deactivated. Empty (still-loading) set never
// flags, so values don't flash orange on open.
const STALE_CLS = 'bg-orange-50 dark:bg-orange-900/20 border-orange-400 dark:border-orange-500';
const STALE_TITLE = 'This value is no longer an active choice — verify or update it.';
const staleCls = (val: any, valid: Set<string>): string => {
  const v = String(val ?? '').trim();
  return v !== '' && valid.size > 0 && !valid.has(v) ? STALE_CLS : '';
};

// The template and the EDI name different locations for this field; the user picks one.
// Nothing is pre-selected — a default here is exactly what the server refuses.
const CandidateChoice: React.FC<{
  group: string;
  candidates: { origin: string; code: number; name: string }[];
  value: number;
  onPick: (c: { code: number; name: string }) => void;
}> = ({ group, candidates, value, onPick }) => (
  <div className="space-y-1">
    <div className="text-[0.6875rem] text-amber-700 dark:text-amber-300">
      The template and the EDI differ — choose one.
    </div>
    {candidates.map((c) => (
      <label key={`${c.origin}-${c.code}`} className="flex items-center gap-2 text-sm text-slate-700 dark:text-gray-200">
        <input
          type="radio"
          name={group}
          className="accent-emerald-600"
          checked={value === c.code}
          onChange={() => onPick({ code: c.code, name: c.name })}
        />
        <span className="text-slate-400 uppercase text-[0.625rem] w-16 shrink-0">{c.origin}</span>
        <span className="truncate">{c.name || `#${c.code}`}</span>
      </label>
    ))}
  </div>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="grid grid-cols-[130px,1fr] items-center gap-3 py-1">
    <label className={LABEL}>{label}</label>
    <div className="min-w-0">{children}</div>
  </div>
);

const EdiCreateModal: React.FC<Props> = ({ recno, title, onClose, onCreated }) => {
  const [preview, setPreview] = useState<EdiCreatePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Editable field state (initialized from the preview, then user-edited).
  const [fields, setFields] = useState<Record<string, any>>({});
  const [carrier, setCarrier] = useState<CarrierValue>({ recno: 0, name: '', scac: '' });
  const [portOrig, setPortOrig] = useState<PortValue>({ recno: 0, name: '' });
  const [portDisch, setPortDisch] = useState<PortValue>({ recno: 0, name: '' });
  const [pickup, setPickup] = useState<LocationValue>({ recno: 0, name: '' });
  const [terminate, setTerminate] = useState<LocationValue>({ recno: 0, name: '' });

  // Option lists.
  const [dispatchers, setDispatchers] = useState<AllDispatcher[]>([]);
  const [rails, setRails] = useState<RailOption[]>([]);
  const [chassisProviders, setChassisProviders] = useState<string[]>([]);
  const [brokers, setBrokers] = useState<LookupOption[]>([]);
  const [billtoContacts, setBilltoContacts] = useState<Contact[]>([]);
  const [brokerContacts, setBrokerContacts] = useState<Contact[]>([]);

  const setField = (k: string, v: any) => setFields((prev) => ({ ...prev, [k]: v }));

  // Load the create-preview.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    getEdiCreatePreview(recno)
      .then((p) => { if (alive) setPreview(p); })
      .catch((e) => { if (alive) setLoadError(e?.response?.data?.detail || e?.message || 'Failed to load the create form.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [recno]);

  // Reference option lists (module-cached in their services).
  useEffect(() => {
    ordersService.getAllDispatchers().then((r) => setDispatchers(r.data)).catch(() => {});
    getRails().then(setRails).catch(() => {});
    ordersService.getChassisProviders().then(setChassisProviders).catch(() => {});
    getBrokers().then(setBrokers).catch(() => {});
  }, []);

  // Seed the editable state once the preview arrives.
  useEffect(() => {
    if (!preview) return;
    setFields({
      ...preview.fields,
      etadate: isoToMdy(preview.fields.etadate),
      lastfreedaydate: isoToMdy(preview.fields.lastfreedaydate),
    });
    setCarrier({ recno: Number(preview.fields.carriercode) || 0, name: '', scac: '' });
    setPortOrig({ recno: Number(preview.fields.portoforigin) || 0, name: String(preview.fields.portoforiginname || '') });
    setPortDisch({ recno: Number(preview.fields.portofdischarge) || 0, name: String(preview.fields.portofdischargename || '') });
    // A 'choice' resolution deliberately carries code 0 — nothing is pre-selected until
    // the user picks, so the picker starts empty rather than silently favouring a side.
    setPickup({ recno: Number(preview.pickup.code) || 0, name: String(preview.pickup.name || '') });
    setTerminate({ recno: Number(preview.terminate.code) || 0, name: String(preview.terminate.name || '') });
  }, [preview]);

  // Bill-to contacts follow the template's bill-to (not editable here).
  useEffect(() => {
    if (!preview) return;
    if (preview.billto) listContacts('billtos', preview.billto).then(setBilltoContacts).catch(() => {});
  }, [preview]);

  // Broker contacts follow the CHOSEN broker — the field is editable, so the list has to
  // re-query when it changes or the dropdown offers another broker's people.
  const brokerCode = String(fields.brokercode ?? '');
  useEffect(() => {
    const code = Number(brokerCode);   // not the `recno` prop — that's the EDI row
    if (!code) { setBrokerContacts([]); return; }
    let alive = true;
    listContacts('brokerforwarder', code)
      .then((c) => { if (alive) setBrokerContacts(c); })
      .catch(() => { if (alive) setBrokerContacts([]); });
    return () => { alive = false; };
  }, [brokerCode]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const dispatcher = String(fields.dispatcher || '');
  const carrierEditable = CARRIER_DISPATCHERS.has(dispatcher);
  const isImport = !!preview?.is_import;
  const etaMissing = isImport && !String(fields.etadate || '').trim();
  // Pickup resolution (server-side; see resolve_edi_pickup). A locked mapping is not
  // editable here, and an unresolved disagreement blocks Create — the server refuses it
  // anyway rather than picking a side.
  const pickupRes = preview?.pickup;
  const pickupLocked = !!pickupRes?.locked;
  const pickupChoice = pickupRes?.source === 'choice';
  const pickupUnresolved = pickupChoice && !pickup.recno;
  // Terminate has no override table, so it is only ever a straight choice or free.
  const terminateRes = preview?.terminate;
  const terminateChoice = terminateRes?.source === 'choice';
  const terminateUnresolved = terminateChoice && !terminate.recno;

  // Changing the dispatcher off an outside carrier clears the carrier (mirrors OrderFormView).
  const onDispatcherChange = (v: string) => {
    setField('dispatcher', v);
    if (!CARRIER_DISPATCHERS.has(v)) setCarrier({ recno: 0, name: '', scac: '' });
  };

  const dispatcherOptions = useMemo(
    () => dispatchers.map((d) => ({ value: d.username, label: d.gwi ? d.displayname : `${d.displayname} (non-GWI)` })),
    [dispatchers],
  );

  // Valid-value sets for the stale-value orange cue (staleCls). Static option lists too, in
  // case a template carries a value that's since dropped off the list.
  const dispatcherSet = useMemo(() => new Set(dispatchers.map((d) => d.username)), [dispatchers]);
  const railSet = useMemo(() => new Set(rails.map((r) => r.terminal)), [rails]);
  const chassisProviderSet = useMemo(() => new Set(chassisProviders), [chassisProviders]);
  const billtoContactSet = useMemo(() => new Set(billtoContacts.map((c) => String(c.recno))), [billtoContacts]);
  const brokerContactSet = useMemo(() => new Set(brokerContacts.map((c) => String(c.recno))), [brokerContacts]);
  const sizeSet = useMemo(() => new Set(EQUIPMENT_SIZES as string[]), []);
  const typeSet = useMemo(() => new Set(EQUIPMENT_TYPES as string[]), []);
  const chassisTypeSet = useMemo(() => new Set(CHASSIS_TYPES as string[]), []);

  const handleCreate = () => {
    if (!preview || saving) return;
    if (etaMissing) { setSaveError('An ETA is required for IMPORT orders.'); return; }
    if (pickupUnresolved) { setSaveError('Choose which pickup location to use.'); return; }
    if (terminateUnresolved) { setSaveError('Choose which terminate location to use.'); return; }
    setSaving(true);
    setSaveError(null);
    // recno-valued fields that are 0/unset go out as '' so the backend skips them and the
    // template's value survives (V1 "form value used only if non-empty").
    const recnoOrBlank = (v: any) => (Number(v) > 0 ? String(Number(v)) : '');
    const payload: Record<string, any> = {
      ...fields,
      etadate: formatBackendDate(fields.etadate) || '',
      lastfreedaydate: formatBackendDate(fields.lastfreedaydate) || '',
      carriercode: recnoOrBlank(carrier.recno),
      portoforigin: recnoOrBlank(portOrig.recno),
      portofdischarge: recnoOrBlank(portDisch.recno),
      billtocontactrecno: recnoOrBlank(fields.billtocontactrecno),
      brokercode: recnoOrBlank(fields.brokercode),
      brokercontactrecno: recnoOrBlank(fields.brokercontactrecno),
    };
    delete payload.portoforiginname;
    delete payload.portofdischargename;
    createEdiOrder(recno, payload, pickup.recno || null, terminate.recno || null)
      .then((res) => onCreated({ recno, pronumber: res.pronumber }))
      .catch((e) => {
        setSaving(false);
        setSaveError(e?.response?.data?.detail || e?.message || 'The order could not be created.');
      });
  };

  const flagSelect = (key: string) => (
    <select className={INPUT} value={String(fields[key] ?? 'N')} onChange={(e) => setField(key, e.target.value)}>
      <option value="N">N</option>
      <option value="Y">Y</option>
    </select>
  );

  const body = () => {
    if (loading) return <div className="p-8 text-center text-sm text-muted">Loading create form…</div>;
    if (loadError) return <div className="m-3 rounded-md border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">{loadError}</div>;
    if (!preview) return null;
    return (
      <div className="flex-1 min-h-0 overflow-auto p-4 space-y-4">
        {!preview.eligible && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
            <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />
            <span>{preview.reason || 'This order is not eligible to create.'}</span>
          </div>
        )}

        {/* Read-only "From EDI" summary (V1 form's middle column). */}
        <div className="rounded-md border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800/50 px-3 py-2">
          <div className="text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400 mb-1">From EDI</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-0.5 text-xs text-slate-700 dark:text-gray-200">
            <div><span className="text-slate-400">Move&nbsp;Type:</span> {preview.movetype}</div>
            <div><span className="text-slate-400">Template:</span> {preview.template}</div>
            <div><span className="text-slate-400">Container:</span> {preview.from_edi.container || '—'}</div>
            <div><span className="text-slate-400">BOL/Bkg:</span> {preview.from_edi.bol || '—'}</div>
            <div><span className="text-slate-400">SCAC:</span> {preview.from_edi.scac || '—'}</div>
            <div><span className="text-slate-400">Broker:</span> {preview.from_edi.broker || '—'}</div>
            <div><span className="text-slate-400">Lbs:</span> {preview.from_edi.lbs || '—'}</div>
            <div><span className="text-slate-400">Kgs:</span> {preview.from_edi.kgs || '—'}</div>
            <div><span className="text-slate-400">Delivery:</span> {preview.delivery.name || '—'}</div>
          </div>
          {preview.from_edi.notes.length > 0 && (
            <div className="mt-1 text-[0.6875rem] text-slate-500 dark:text-gray-400 whitespace-pre-wrap">
              {preview.from_edi.notes.join('\n')}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6">
          <div>
            <Row label="Master Pro">
              <input className={INPUT} value={String(fields.masterpro ?? '')} onChange={(e) => setField('masterpro', e.target.value)} placeholder="(defaults to the new pro #)" />
            </Row>
            <Row label="Pickup">
              {pickupLocked ? (
                <div>
                  <span className="text-sm text-slate-700 dark:text-gray-200">{pickup.name || `#${pickup.recno}`}</span>
                  <span className="ml-2 text-[0.6875rem] text-slate-500 dark:text-gray-400">
                    (locked — from EDI Pickup Location Mapping)
                  </span>
                </div>
              ) : pickupChoice ? (
                <CandidateChoice
                  group="edi-create-pickup"
                  candidates={pickupRes?.candidates ?? []}
                  value={pickup.recno}
                  onPick={(c) => setPickup({ recno: c.code, name: c.name })}
                />
              ) : (
                <LocationLookupField value={pickup} onChange={setPickup} label="Pickup" hideLabel hideStatus />
              )}
            </Row>
            <Row label="Delivery">
              <span className="text-sm text-slate-500 dark:text-gray-400">{preview.delivery.name || '—'} <span className="text-[0.6875rem]">(from mapping)</span></span>
            </Row>
            <Row label="Terminate">
              {terminateChoice ? (
                <CandidateChoice
                  group="edi-create-terminate"
                  candidates={terminateRes?.candidates ?? []}
                  value={terminate.recno}
                  onPick={(c) => setTerminate({ recno: c.code, name: c.name })}
                />
              ) : (
                <LocationLookupField value={terminate} onChange={setTerminate} label="Terminate" hideLabel hideStatus />
              )}
            </Row>
            <Row label="Port of Origin">
              <PortLookupField value={portOrig} onChange={setPortOrig} label="Port of Origin" />
            </Row>
            <Row label="Port of Discharge">
              <PortLookupField value={portDisch} onChange={setPortDisch} label="Port of Discharge" />
            </Row>
            <Row label="Terminal">
              <select className={`${INPUT} ${staleCls(fields.rail, railSet)}`} title={staleCls(fields.rail, railSet) ? STALE_TITLE : undefined}
                value={String(fields.rail ?? '')} onChange={(e) => setField('rail', e.target.value)}>
                <option value="">—</option>
                {rails.map((r) => <option key={r.terminal} value={r.terminal}>{r.terminal}</option>)}
              </select>
            </Row>
            <Row label="Vessel">
              <input className={INPUT} value={String(fields.vessel ?? '')} onChange={(e) => setField('vessel', e.target.value.toUpperCase())} />
            </Row>
            <Row label="Voyage">
              <input className={INPUT} value={String(fields.voyage ?? '')} onChange={(e) => setField('voyage', e.target.value)} />
            </Row>
            <Row label="Commodity">
              <input className={INPUT} value={String(fields.commodity ?? '')} onChange={(e) => setField('commodity', e.target.value)} />
            </Row>
          </div>

          <div>
            <Row label="Bill To Contact">
              <select className={`${INPUT} ${staleCls(fields.billtocontactrecno, billtoContactSet)}`}
                title={staleCls(fields.billtocontactrecno, billtoContactSet) ? STALE_TITLE : undefined}
                value={String(fields.billtocontactrecno ?? '')} onChange={(e) => setField('billtocontactrecno', e.target.value)}>
                <option value="">—</option>
                {billtoContacts.map((c) => <option key={c.recno} value={c.recno}>{c.contactname}{c.contactphone ? ` · ${c.contactphone}` : ''}</option>)}
              </select>
            </Row>
            <Row label="Broker">
              <TypeAhead
                options={brokers}
                value={brokerCode && brokerCode !== '0' ? brokerCode : ''}
                onChange={(next) => {
                  setFields((prev) => ({
                    ...prev,
                    brokercode: next || '',
                    // A contact belongs to one broker — the old pick cannot carry over.
                    brokercontactrecno: '',
                  }));
                }}
                placeholder="Search broker/forwarder…"
                showSearch
                showDescriptions
                compact
                ariaLabel="Broker/Forwarder"
              />
            </Row>
            <Row label="Broker Contact">
              <select className={`${INPUT} ${staleCls(fields.brokercontactrecno, brokerContactSet)}`}
                title={staleCls(fields.brokercontactrecno, brokerContactSet) ? STALE_TITLE : undefined}
                value={String(fields.brokercontactrecno ?? '')} onChange={(e) => setField('brokercontactrecno', e.target.value)}>
                <option value="">—</option>
                {brokerContacts.map((c) => <option key={c.recno} value={c.recno}>{c.contactname}{c.contactphone ? ` · ${c.contactphone}` : ''}</option>)}
              </select>
            </Row>
            <Row label={isImport ? 'ETA *' : 'ETA'}>
              <div>
                <DateTimePicker
                  mode="date" inputVariant="compact" selectionType="single" autoApply usePortal
                  openDirection="down" autoFlip labelType="none" placeholder="ETA"
                  className="w-full [&_div]:!min-w-0"
                  value={parseMdYDate(String(fields.etadate ?? ''))}
                  onChange={(v) => setField('etadate', formatMdYDate(v as Date | null))}
                />
                {etaMissing && <span className="text-[0.6875rem] text-red-600 dark:text-red-400">ETA is required for IMPORT.</span>}
              </div>
            </Row>
            <Row label="LFD">
              <DateTimePicker
                mode="date" inputVariant="compact" selectionType="single" autoApply usePortal
                openDirection="down" autoFlip labelType="none" placeholder="LFD"
                className="w-full [&_div]:!min-w-0"
                value={parseMdYDate(String(fields.lastfreedaydate ?? ''))}
                onChange={(v) => setField('lastfreedaydate', formatMdYDate(v as Date | null))}
              />
            </Row>
            <Row label="Dispatcher">
              <select className={`${INPUT} ${staleCls(dispatcher, dispatcherSet)}`} title={staleCls(dispatcher, dispatcherSet) ? STALE_TITLE : undefined}
                value={dispatcher} onChange={(e) => onDispatcherChange(e.target.value)}>
                <option value="">—</option>
                {dispatcherOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                {dispatcher && !dispatcherOptions.some((o) => o.value === dispatcher) && <option value={dispatcher}>{dispatcher}</option>}
              </select>
            </Row>
            <Row label="Carrier">
              {carrierEditable ? (
                <CarrierLookupField value={carrier} onChange={setCarrier} />
              ) : (
                <span className="text-sm text-slate-400 dark:text-gray-500">{carrier.name || '—'} <span className="text-[0.6875rem]">(locked — dispatcher is not an outside carrier)</span></span>
              )}
            </Row>
            <Row label="Size / Type">
              <div className="flex gap-2">
                <select className={`${INPUT} ${staleCls(fields.size, sizeSet)}`} title={staleCls(fields.size, sizeSet) ? STALE_TITLE : undefined}
                  value={String(fields.size ?? '')} onChange={(e) => setField('size', e.target.value)}>
                  {EQUIPMENT_SIZES.map((s) => <option key={s} value={s}>{s || '—'}</option>)}
                </select>
                <select className={`${INPUT} ${staleCls(fields.equiptype, typeSet)}`} title={staleCls(fields.equiptype, typeSet) ? STALE_TITLE : undefined}
                  value={String(fields.equiptype ?? '')} onChange={(e) => setField('equiptype', e.target.value)}>
                  {EQUIPMENT_TYPES.map((t) => <option key={t} value={t}>{t || '—'}</option>)}
                </select>
              </div>
            </Row>
            <Row label="Chassis">
              <div className="flex gap-2">
                <select className={`${INPUT} ${staleCls(fields.chassisprovider, chassisProviderSet)}`}
                  title={staleCls(fields.chassisprovider, chassisProviderSet) ? STALE_TITLE : undefined}
                  value={String(fields.chassisprovider ?? '')} onChange={(e) => setField('chassisprovider', e.target.value)}>
                  <option value="">—</option>
                  {chassisProviders.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select className={`${INPUT} ${staleCls(fields.chassistype, chassisTypeSet)}`}
                  title={staleCls(fields.chassistype, chassisTypeSet) ? STALE_TITLE : undefined}
                  value={String(fields.chassistype ?? '')} onChange={(e) => setField('chassistype', e.target.value)}>
                  {CHASSIS_TYPES.map((t) => <option key={t} value={t}>{t || '—'}</option>)}
                </select>
              </div>
            </Row>
            <Row label="Drop">{flagSelect('isdrop')}</Row>
            <Row label="Hot">{flagSelect('hot')}</Row>
            <Row label="Hazmat">{flagSelect('hazmat')}</Row>
            <Row label="Comments">
              <textarea className={INPUT} rows={3} value={String(fields.comments ?? '')} onChange={(e) => setField('comments', e.target.value)} />
            </Row>
          </div>
        </div>
      </div>
    );
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[130] bg-slate-900/40 flex items-center justify-center p-6"
      role="dialog"
      aria-label="Create Order from EDI"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-slate-200 dark:border-gray-700 w-full max-w-4xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-heading">Create Order</h2>
            <p className="text-xs text-slate-500 dark:text-gray-400 truncate">{title}</p>
          </div>
          <button type="button" onClick={() => !saving && onClose()} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {body()}

        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-slate-200 dark:border-gray-700">
          <div className="text-xs text-red-600 dark:text-red-400 truncate">{saveError}</div>
          <div className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={() => !saving && onClose()}
              className="px-4 py-1.5 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600">
              Cancel
            </button>
            <button type="button" onClick={handleCreate}
              disabled={saving || loading || !preview?.eligible || etaMissing || pickupUnresolved || terminateUnresolved}
              className="px-5 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
              {saving ? 'Creating…' : 'Create Order'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EdiCreateModal;
